import { useMemo } from 'react';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useRouter, usePathname } from 'next/navigation';
import { getProfileUrl } from 'src/utils/profileUrl';
import { format as formatDate } from 'date-fns';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faXmark,
  faHospital,
  faTv,
} from '@fortawesome/pro-solid-svg-icons';
import { PulseIconButton } from 'src/components/PulseUI/PulseIconButton';
import { RetiredChip } from 'src/components/PulseUI/RetiredChip';
import { ProfileDrawerActions } from '../ProfileDrawerActions';
import { useGetProviderStrategyTabsQuery } from 'src/api/redux/strategy/chainedStrategiesApi';
import StrategyChainsCell from 'src/components/Table/StrategyChainsCell';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { formatSocClassification } from 'src/components/Profiles/components/ProfileDetails/socTypes';
import { ENTITY_TYPE_LABELS } from './constants';


interface ProfileDrawerHeaderV2Props {
  onClose?: () => void;
}

const ProfileDrawerHeaderV2 = ({ onClose }: ProfileDrawerHeaderV2Props) => {
  const { entityDetails } = useProfile();
  const router = useRouter();
  const pathName = usePathname();
  const isFromStrategyCenter = pathName?.includes('strategy-center');

  const { data: strategyTabsResponse } = useGetProviderStrategyTabsQuery(
    { providerId: entityDetails?.id || '' },
    { skip: !isFromStrategyCenter || !entityDetails?.id }
  );

  const { providerChainIds, chainsMetadata } = useMemo(() => {
    const chains = strategyTabsResponse?.results || [];
    return {
      providerChainIds: chains.map((c: any) => c.id),
      chainsMetadata: chains.reduce(
        (acc: Record<number, { label: string }>, c: any) => {
          acc[c.id] = { label: c.label || c.name || `Strategy ${c.id}` };
          return acc;
        },
        {}
      ),
    };
  }, [strategyTabsResponse]);

  const isRetired = entityDetails?.retired;
  const deactivatedDate = entityDetails?.deactivation_date
    ? formatDate(new Date(entityDetails?.deactivation_date), 'MM/dd/yyyy')
    : null;

  const handleProfileClick = () => {
    onClose?.();

    const { providerId: profileId, profileType } = getEntityIdentifiers(entityDetails);

    router.push(getProfileUrl(profileType, profileId, 'plays'));
  };

  return (
    <div className="flex flex-col gap-4 p-4 w-full bg-sky-800">
      <div className="flex items-start justify-between ">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold text-white">
            {entityDetails?.name}
          </span>
          {isRetired && <RetiredChip deactivatedDate={deactivatedDate} />}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-white font-semibold block">
              <Icon icon={faHospital} className="mr-1" />
              {entityDetails?.specialty ||
                formatSocClassification(entityDetails) ||
                ENTITY_TYPE_LABELS[entityDetails?.type] ||
                'Provider Profile'}
            </span>
            <span className="text-xs text-white font-semibold block">
              <Icon icon={faMapMarkerAlt} className="mr-1" />
              {entityDetails?.city}, {entityDetails?.state}
            </span>
          </div>
          {isFromStrategyCenter && providerChainIds.length > 0 && (
            <StrategyChainsCell
              chainIds={providerChainIds}
              chainsMetadata={chainsMetadata}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <PulseIconButton
            icon={faTv}
            variant="plain"
            className="bg-neutral-50/50 text-white hover:text-sky-600"
            onClick={handleProfileClick}
            aria-label="Open Full Profile"
            size="sm"
          />
          <PulseIconButton
            icon={faXmark}
            variant="plain"
            className="bg-neutral-50/50 text-white hover:text-sky-600"
            onClick={onClose}
            aria-label="Close Profile Drawer"
            size="sm"
          />
        </div>
      </div>
      <ProfileDrawerActions provider={entityDetails} />
    </div>
  );
};

export default ProfileDrawerHeaderV2;
